// VÁZLAT-TÁR (autosave) — a félbehagyott jegy-válasz / üzenet a böngészőben, a felhasználóhoz és a helyhez kötve.
//
// MIÉRT a böngészőben (és nem szerver-oldali piszkozat-tábla): a vázlat egy NEM ELKÜLDÖTT, személyes munkapéldány —
// senki más nem látja, nem hat semmilyen üzleti állapotra, és a cél az, hogy egy véletlen bezárás / frissítés /
// fül-váltás ne vigye el a gépelt szöveget. Ez a bevett gyakorlat a jegykezelőkben (a vázlat a beviteli mező
// „emlékezete"), és nem kell hozzá séma vagy jog.
//
// BIZTONSÁG ÉS ADATVÉDELEM:
//   • a kulcs a felhasználóhoz kötött (`atlas.draft.v1:<sub>:<hely>`) — egy közös gépen a másik fiók nem látja;
//   • KIJELENTKEZÉSKOR minden vázlat törlődik (`clearAllDrafts`) — a szöveg nem marad a gépen a munkamenet után; a
//     törlés egy GENERÁCIÓT is léptet, így a törlés UTÁN befutó (még függő) mentés sem írhatja vissza a szöveget;
//   • a munkamenet LEJÁRTA (401) NEM töröl: az újra-belépés utáni folytatás épp a vázlat célja — a lejárat (14 nap)
//     és a kifejezett kijelentkezés a határ;
//   • 14 nap után a vázlat lejár (egy elfelejtett félmondat nem kísért hetekig);
//   • a tároló elérhetetlensége (privát mód, letiltott storage) NEM hiba: a felület vázlat nélkül működik.
const PREFIX = 'atlas.draft.v1:';
/** Egy vázlat élettartama: két hét. */
export const DRAFT_TTL_MS = 14 * 24 * 60 * 60 * 1000;

interface StoredDraft<T> {
  value: T;
  savedAt: number;
}

/** A vázlat-tár generációja — a `clearAllDrafts` lépteti; a régebbi generációban indított függő mentés elvész. */
let generation = 0;
export function draftGeneration(): number {
  return generation;
}

export function draftKey(sub: string, scope: string): string {
  return `${PREFIX}${sub}:${scope}`;
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    // a hozzáférés maga is dobhat (letiltott sütik / privát mód) — vázlat nélkül megyünk tovább
    return null;
  }
}

/** A tárolt vázlat (lejárt / sérült / hiányzó → null; a lejártat törli is). */
export function readDraft<T>(key: string, now = Date.now()): StoredDraft<T> | null {
  const s = storage();
  if (!s) return null;
  try {
    const raw = s.getItem(key);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<StoredDraft<T>>;
    if (typeof parsed.savedAt !== 'number' || !('value' in parsed)) {
      s.removeItem(key);
      return null;
    }
    if (now - parsed.savedAt > DRAFT_TTL_MS) {
      s.removeItem(key);
      return null;
    }
    return { value: parsed.value, savedAt: parsed.savedAt };
  } catch {
    // sérült JSON vagy tiltott hozzáférés → nincs vázlat (a következő mentés felülírja)
    return null;
  }
}

export function writeDraft<T>(key: string, value: T, now = Date.now()): boolean {
  const s = storage();
  if (!s) return false;
  try {
    s.setItem(key, JSON.stringify({ value, savedAt: now } satisfies StoredDraft<T>));
    return true;
  } catch {
    // betelt kvóta / tiltott tároló → a vázlat ezúttal nem mentődik, a gépelés zavartalan
    return false;
  }
}

/** A vázlat törlése; `false`, ha a tároló nem elérhető (ilyenkor nincs is mit törölni). */
export function removeDraft(key: string): boolean {
  const s = storage();
  if (!s) return false;
  try {
    s.removeItem(key);
    return true;
  } catch {
    // tiltott tároló → nincs mit törölni
    return false;
  }
}

/** MINDEN vázlat törlése (kijelentkezéskor) — minden felhasználóé, mert a gép a munkamenet után nem őriz szöveget. */
export function clearAllDrafts(): number {
  generation += 1;
  const s = storage();
  if (!s) return 0;
  try {
    const keys: string[] = [];
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i);
      if (k?.startsWith(PREFIX)) keys.push(k);
    }
    for (const k of keys) s.removeItem(k);
    return keys.length;
  } catch {
    // tiltott tároló → nincs mit törölni
    return 0;
  }
}
